import { useCallback, type ChangeEvent, type Dispatch, type FocusEvent, type InputHTMLAttributes, type Ref, type SetStateAction } from 'react'
import { InputFrame, type InputFrameProps } from '../layout/InputFrame'
import { cn } from '@/utilities/misc'

export type TextBoxProps =
  Omit<InputFrameProps, 'height' | 'children'> &
  InputHTMLAttributes<HTMLInputElement> &
  {
    ref?: Ref<HTMLInputElement | null>,
    setValue?: Dispatch<SetStateAction<string>>,
    rewriteOnBlur?: (value: string) => any,
    frameClassName?: string
  }

export function TextBox({
  ref,
  label,
  prefix,
  suffix,
  tooltip,
  tooltipPlacement = 'bottom',
  required,
  disabled,
  width,
  className,
  frameClassName,
  children,
  onChange,
  setValue,
  rewriteOnBlur,
  ...rest
}: TextBoxProps) {

  className = cn(
    'TextBox',
    className,
  )

  //--------------------------------------------------------------------------------
  const handleChange = useCallback((ev: ChangeEvent<HTMLInputElement>) => {
    if (onChange)
      onChange(ev)
    if (setValue)
      setValue(ev.target.value)
  }, [setValue, onChange])

  //--------------------------------------------------------------------------------
  const handleBlur = useCallback((ev: FocusEvent<HTMLInputElement>) => {
    if (rewriteOnBlur && ev.target.value) {
      const newValue = rewriteOnBlur(ev.target.value)
      if (newValue === undefined || newValue === null) return ''
      setValue?.(newValue.toString() ?? '')
    }
  }, [rewriteOnBlur, setValue])

  //--------------------------------------------------------------------------------
  return (
    <InputFrame
      label={label}
      prefix={prefix}
      suffix={suffix}
      tooltip={tooltip}
      tooltipPlacement={tooltipPlacement}
      required={required}
      disabled={disabled}
      width={width}
      className={cn('TextBox', frameClassName)}
    >
      <input
        ref={ref}
        className={className}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        {...rest}
      />
    </InputFrame>
  )
}
